import { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';

import { DataTablePaginationProps, useDataTablePagination } from './useDataTablePagination';

interface DataTablePaginationComponentProps<TData> extends DataTablePaginationProps<TData> {
  centerContent?: ReactNode;
}

export function DataTablePagination<TData>(props: DataTablePaginationComponentProps<TData>) {
  const { t } = useTranslation();
  const { pageIndex, canPreviousPage, canNextPage, handlePreviousPage, handleNextPage, pageCount } =
    useDataTablePagination(props);

  const hasMultiplePages = pageCount > 1;

  return (
    <div className="mt-4 grid h-10 grid-cols-3 items-center pl-1">
      {/* LEFT: Resource count + page info */}
      <div className="flex items-center space-x-2 text-sm text-foreground-muted">
        <span>
          {props.resourceName
            ? t(`table.resource.${props.resourceName}`, { count: props.rowCount })
            : props.rowCount}
        </span>
        {hasMultiplePages && (
          <>
            <span className="text-xs leading-none text-foreground-muted">·</span>
            <span>{t('table.page', { 0: pageIndex + 1, 1: pageCount })}</span>
          </>
        )}
      </div>

      {/* CENTER: Optional content (always centered) */}
      <div className="flex justify-center">{props.centerContent}</div>

      {/* RIGHT: Navigation buttons */}
      <div className="flex items-center justify-end space-x-2">
        {pageCount > 1 && (
          <>
            <Button
              variant="outline-neutral"
              size="sm"
              onClick={handlePreviousPage}
              disabled={!canPreviousPage}
            >
              {t('table.previous')}
            </Button>
            <Button
              variant="outline-neutral"
              size="sm"
              onClick={handleNextPage}
              disabled={!canNextPage}
            >
              {t('table.next')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
